import * as t from 'io-ts';
import _, {isEqual, without} from 'lodash';

import {serializeConversationPatch} from '../../../libs/shared/core/src/models/api/conversationPatchModel';
import {ConversationTrackerFlagsEnum} from '../../../libs/shared/core/src/models/api/conversationTrackerModel';
import {makeOptional} from '../../../libs/shared/core/src/models/api/types/optionalType';
import {patchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../libs/shared/core/src/services/http/httpError';
import {createCountersUpdaterForConversations} from '../../../src/helpers/counterHelpers';
import {ApiConversationBatch} from '../../../src/models/api/conversationBatchModel';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {DataGroupModel, mergeDataGroups} from '../../../src/models/api/dataGroupModel';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {updateClientCountersForConversationsAction} from '../../../src/store/data/counters/countersActions';
import {dataUpdatedAction} from '../../../src/store/data/dataGroupsActions';
import {OfflineError} from '../../../srcCommon/services/offline/offlineError';
import {
  composeUpdaters,
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../helpers/updateHelpers';
import {createTaskType} from '../task';

export const RUpdateConversationsSeenTask = createTaskType({
  name: 'updateConversationsSeen',
  payloadType: t.type({
    conversations: t.readonlyArray(
      t.type({
        id: t.number,
        seenAt: t.number,
        lastSeenUpdatedAt: makeOptional(t.number),
        lastActivityAt: t.number,
        offlineSeenId: t.string,
      }),
    ),
    teammateId: t.number,
    initiatedAt: makeOptional(t.number),
  }),
  createOptimisticUpdater: ({conversations, teammateId}) =>
    createCountersUpdaterForConversations(
      conversations.map(({id}) => id),
      createObjectPropertyUpdater(
        'conversations',
        createObjectPropertyUpdater(
          'byId',
          composeUpdaters(
            ...conversations.map(({id, seenAt, lastSeenUpdatedAt, offlineSeenId}) =>
              createResourceMapValueUpdater(
                id,
                createUpdater<ConversationModel>((conversation) => {
                  // If this action conflicts, ignore.
                  const isConflictingUpdate =
                    lastSeenUpdatedAt !== conversation.seenUpdatedAt &&
                    offlineSeenId !== conversation.offlineSeenId;
                  if (isConflictingUpdate) {
                    return conversation;
                  }

                  // Clear the tracker unsnoozed flag, if present.
                  const tracker = conversation.trackers[teammateId];
                  const newTracker = tracker && {
                    ...tracker,
                    extra: without(tracker.extra, ConversationTrackerFlagsEnum.UNSNOOZED),
                  };
                  const newTrackers =
                    newTracker && !isEqual(newTracker, tracker)
                      ? {
                          ...conversation.trackers,
                          [teammateId]: newTracker,
                        }
                      : conversation.trackers;

                  return {
                    ...conversation,
                    seenAt,
                    offlineSeenId,
                    trackers: newTrackers,
                    // Clear the bump activity.
                    bumpActivityId: undefined,
                    // Clear the team unsnooze flag for us, because we saw the conversation since it team unsnoozed.
                    isUnsnoozed: false,
                  };
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  createPerformer:
    ({conversations, teammateId, initiatedAt}) =>
    async (dispatch, getState, finishOptimisticUpdate) => {
      try {
        const company = getCompany(getState());
        const url = `${company.url}/conversations`;
        const body = {
          conversations: conversations.map(({id, seenAt, lastSeenUpdatedAt, lastActivityAt, offlineSeenId}) =>
            serializeConversationPatch(id, {
              seenAt,
              offlineMetadata: {
                trackers: [
                  {
                    teammateId,
                    lastSeenUpdatedAt,
                    lastActivityAt,
                    offlineSeenId,
                  },
                ],
                initiatedAt,
              },
            }),
          ),
        };
        const {dataGroups} = await patchApiAsync(ApiConversationBatch, url, body);
        if (dataGroups.length !== conversations.length) {
          throw new Error(
            `Expected ${conversations.length} conversation batch results, received ${dataGroups.length}`,
          );
        }

        const dataGroupsWithOfflineIds = _(conversations)
          .zip(dataGroups)
          .map(
            ([conversation, dataGroup]) =>
              conversation && dataGroup && applyOfflineSeenId(conversation.offlineSeenId, dataGroup),
          )
          .compact()
          .value();
        const dataGroup = mergeDataGroups(...dataGroupsWithOfflineIds);

        // Update our state + finish the optimistic update + update client-side counters.
        // TODO: Make this prettier and reduce duplication.
        dispatch(
          updateClientCountersForConversationsAction(
            conversations.map(({id}) => id),
            () => {
              finishOptimisticUpdate(dataUpdatedAction(dataGroup));
            },
          ),
        );
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        throw error;
      }
    },
});

function applyOfflineSeenId(requestedOfflineId: string, dataGroup: DataGroupModel): DataGroupModel {
  const {conversations, acceptedOfflineIds} = dataGroup;
  if (!conversations) {
    return dataGroup;
  }

  const offlineSeenId =
    acceptedOfflineIds && acceptedOfflineIds.includes(requestedOfflineId) ? requestedOfflineId : undefined;

  return {
    ...dataGroup,
    conversations: conversations.map((conversation) => ({
      ...conversation,
      offlineSeenId,
    })),
  };
}
